// Mirrors apps/hire/app/services/sms_sender/length.rb. Bird rejects an SMS over 12 segments;
// one character outside GSM 03.38 (an emoji, a curly quote) makes the whole body UCS-2.
// https://bird.com/docs/guides/sms/character-limits
export const MAX_SEGMENTS = 12;
export const GSM_7_SEGMENT_UNITS = 153;
export const UCS_2_SEGMENT_UNITS = 67;

// contract-mirrors reads both tables as double-quoted literals.
// prettier-ignore
const GSM_7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
// Each costs two septets.
// prettier-ignore
const GSM_7_EXTENSION = "^{}\\[~]|€\f";

const GSM_7_CHARS = new Set([...GSM_7_BASIC, ...GSM_7_EXTENSION]);
const GSM_7_EXTENSION_CHARS = new Set([...GSM_7_EXTENSION]);

export function measureSmsLength(text) {
  const chars = [...(text || '')];

  if (chars.every(char => GSM_7_CHARS.has(char))) {
    const units = chars.reduce(
      (sum, char) => sum + (GSM_7_EXTENSION_CHARS.has(char) ? 2 : 1),
      0,
    );
    const limit = MAX_SEGMENTS * GSM_7_SEGMENT_UNITS;
    return { encoding: 'gsm_7', units, limit, exceedsLimit: units > limit };
  }

  // Length is in UTF-16 code units, so an astral emoji counts twice.
  const units = (text || '').length;
  const limit = MAX_SEGMENTS * UCS_2_SEGMENT_UNITS;
  return { encoding: 'ucs_2', units, limit, exceedsLimit: units > limit };
}

// A merge key's value is known only per applicant. Each counts at a typical value's length, and any
// one can carry a character outside GSM 03.38 (an accented or emoji name), so a body with merge
// keys is measured as UCS-2. Some warnings are false; delivery fails only what goes over.
// A URL key counts the same: delivery shortens any link of 35 or more characters
// (Messaging::TextWithLinksShortener), except where the opening's country forbids it.
export const MERGE_KEY_ESTIMATE_UNITS = 30;
// Saving a template upcases each bracketed span that holds a key and deletes every other one
// (MessageTemplate.remove_invalid_data_keys_from_text); the renderer then fills each key.
const BRACKETED_PATTERN = /\[[^\n]*?\]/g;
const KEY_SHAPED_PATTERN = /\[[A-Za-z\d]+(?:_[A-Za-z\d]+)*\]/;
const RENDERED_KEY_PATTERN = /\[[A-Z\d]+(?:_[A-Z\d]+)*\]/g;
// Messaging::TextWithLinksShortener swaps a link of URL_LENGTH_THRESHOLD or more characters for a
// shorter one before delivery, so each counts at one character under the threshold. Accounts in
// these countries send links in full (DeliverableSmsMessageDecorator#url_shortening_not_allowed?).
export const NO_LINK_SHORTENING_COUNTRIES = ['PH', 'IN'];
const LINK_PATTERN = /https?:\/\/[^\s]+[^?.,!\s]/g;
const URL_LENGTH_THRESHOLD = 35;
const SHORT_LINK_STAND_IN = 'x'.repeat(URL_LENGTH_THRESHOLD - 1);

function withShortenedLinks(text) {
  return text.replace(LINK_PATTERN, link =>
    link.length < URL_LENGTH_THRESHOLD ? link : SHORT_LINK_STAND_IN,
  );
}

export function estimateRenderedSmsLength(text, { shortensLinks = true } = {}) {
  const keptText = (text || '').replace(BRACKETED_PATTERN, bracketed =>
    KEY_SHAPED_PATTERN.test(bracketed) ? bracketed.toUpperCase() : '',
  );
  const savedText = shortensLinks ? withShortenedLinks(keptText) : keptText;
  const mergeKeyCount = (savedText.match(RENDERED_KEY_PATTERN) || []).length;
  if (mergeKeyCount === 0) {
    const length = measureSmsLength(savedText);
    return {
      ...length,
      hasMergeKeys: false,
      fixedTextExceedsLimit: length.exceedsLimit,
    };
  }

  const fixedText = savedText.replace(RENDERED_KEY_PATTERN, '');
  const units = fixedText.length + mergeKeyCount * MERGE_KEY_ESTIMATE_UNITS;
  const limit = MAX_SEGMENTS * UCS_2_SEGMENT_UNITS;
  return {
    encoding: 'ucs_2',
    units,
    limit,
    exceedsLimit: units > limit,
    hasMergeKeys: true,
    // Over the limit without its merge keys, so no applicant's values can bring it under.
    fixedTextExceedsLimit: measureSmsLength(fixedText).exceedsLimit,
  };
}
